type Listener = (event: { type: string; payload: unknown }) => void;

export type WsState = "connecting" | "open" | "closed" | "reconnecting";

export interface WsClient {
  connect(): void;
  disconnect(): void;
  on(listener: Listener): () => void;
  state(): WsState;
}

export function createWsClient(path: string): WsClient {
  let socket: WebSocket | null = null;
  const listeners = new Set<Listener>();
  let attempts = 0;
  let state: WsState = "closed";
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let manualClose = false;

  function open() {
    state = attempts === 0 ? "connecting" : "reconnecting";
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    socket = new WebSocket(`${proto}//${window.location.host}${path}`);
    socket.onopen = () => {
      state = "open";
      attempts = 0;
    };
    socket.onmessage = (e: MessageEvent) => {
      let msg: { type: string; payload: unknown };
      try {
        msg = JSON.parse(e.data) as { type: string; payload: unknown };
      } catch (err) {
        console.warn("[ws] malformed frame — skipping", err);
        return;
      }
      listeners.forEach((l) => l(msg));
    };
    socket.onerror = () => {
      state = "closed";
      socket?.close();
    };
    socket.onclose = (e: CloseEvent) => {
      state = "closed";
      socket = null;
      if (manualClose || e.code === 1000) return;
      const delay = Math.min(30_000, 1000 * 2 ** attempts);
      attempts += 1;
      reconnectTimer = setTimeout(open, delay);
    };
  }

  return {
    connect() {
      manualClose = false;
      if (!socket) open();
    },
    disconnect() {
      manualClose = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      socket?.close(1000);
    },
    on(l) {
      listeners.add(l);
      return () => {
        listeners.delete(l);
      };
    },
    state: () => state,
  };
}
